import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Square, Brain, Zap, Volume2, BarChart3, Globe, Clock, CheckCircle, XCircle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import VoiceWaves from './VoiceWaves';

const MOCK_LOGS = [
  { id: 1, type: 'stt', label: 'STT', message: 'Audio captured, processing...', time: '0.0s' },
  { id: 2, type: 'stt', label: 'STT', message: 'Transcription: "Qual o status do servidor?"', time: '0.4s' },
  { id: 3, type: 'urp', label: 'URP', message: 'Routing to brain provider...', time: '0.6s' },
  { id: 4, type: 'urp', label: 'URP', message: 'Selected: Local via Ollama', time: '0.8s' },
  { id: 5, type: 'stt', label: 'STT', message: 'Confidence: 0.97', time: '1.0s' },
  { id: 6, type: 'urp', label: 'URP', message: 'URP rule match: cost_optimizer', time: '1.2s' },
  { id: 7, type: 'brain', label: 'BRAIN', message: 'Inference started (Qwen2.5:7b)', time: '1.5s' },
  { id: 8, type: 'brain', label: 'BRAIN', message: 'Response generated (320ms)', time: '2.1s' },
  { id: 9, type: 'tts', label: 'TTS', message: 'Synthesizing speech via Piper...', time: '2.3s' },
  { id: 10, type: 'tts', label: 'TTS', message: 'Audio output ready', time: '2.8s' },
];

const LATENCY_DATA = [
  { name: 'STT', ms: 320 },
  { name: 'URP', ms: 45 },
  { name: 'Brain', ms: 890 },
  { name: 'TTS', ms: 410 },
  { name: 'Total', ms: 1665 },
];

const mockCostAvoided = {
  total: '$0.042',
  externalCalls: 0,
  localCalls: 3,
  savingsVsCloud: '$0.31',
};

function VoiceSession({ config, onStop }) {
  const [active, setActive] = useState(true);
  const [micOn, setMicOn] = useState(false);
  const [logs, setLogs] = useState([]);
  const [showReport, setShowReport] = useState(false);
  const logIndex = useRef(0);
  const logContainer = useRef(null);

  useEffect(() => {
    if (!active) return;
    const interval = setInterval(() => {
      if (logIndex.current < MOCK_LOGS.length) {
        setLogs((prev) => [...prev, MOCK_LOGS[logIndex.current]]);
        logIndex.current += 1;
      } else {
        clearInterval(interval);
      }
    }, 600);
    return () => clearInterval(interval);
  }, [active]);

  useEffect(() => {
    if (logContainer.current) {
      logContainer.current.scrollTop = logContainer.current.scrollHeight;
    }
  }, [logs]);

  const toggleMic = () => {
    setMicOn((m) => !m);
  };

  const handleStop = () => {
    setActive(false);
    setMicOn(false);
    setShowReport(true);
  };

  if (showReport) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white">Session Complete</h2>
            <p className="text-slate-400 mt-1">Post-call ROI Report</p>
          </div>

          {/* Cost Avoided */}
          <div className="glass-panel p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-400" />
              Cost Analysis
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl bg-slate-800/50">
                <p className="text-xs text-slate-500 uppercase">Total Cost</p>
                <p className="text-2xl font-bold text-white mt-1">{mockCostAvoided.total}</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-800/50">
                <p className="text-xs text-slate-500 uppercase">Local Calls</p>
                <p className="text-2xl font-bold text-emerald-400 mt-1">{mockCostAvoided.localCalls}</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-800/50">
                <p className="text-xs text-slate-500 uppercase">External Calls</p>
                <p className="text-2xl font-bold text-amber-400 mt-1">{mockCostAvoided.externalCalls}</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-800/50">
                <p className="text-xs text-slate-500 uppercase">Savings vs Cloud</p>
                <p className="text-2xl font-bold text-emerald-400 mt-1">{mockCostAvoided.savingsVsCloud}</p>
              </div>
            </div>
          </div>

          {/* Latency Graph */}
          <div className="glass-panel p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" />
              Latency Breakdown
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={LATENCY_DATA}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="name" stroke="#64748B" tick={{ fill: '#94A3B8' }} />
                  <YAxis stroke="#64748B" tick={{ fill: '#94A3B8' }} unit="ms" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1E293B',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      color: '#F8FAFC',
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="ms"
                    stroke="#A855F7"
                    strokeWidth={2}
                    dot={{ fill: '#A855F7', strokeWidth: 0, r: 5 }}
                    activeDot={{ r: 7, fill: '#A855F7' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Model Trace */}
          <div className="glass-panel p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-400" />
              Model Trace
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-xl bg-slate-800/50">
                <div className="flex items-center gap-3">
                  <Brain className="w-5 h-5 text-purple-400" />
                  <div>
                    <p className="text-sm font-medium text-white">Brain</p>
                    <p className="text-xs text-slate-500">{config?.brain?.name} via {config?.brain?.provider}</p>
                  </div>
                </div>
                <span className="text-xs text-emerald-400 font-medium">890ms</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl bg-slate-800/50">
                <div className="flex items-center gap-3">
                  <Volume2 className="w-5 h-5 text-amber-400" />
                  <div>
                    <p className="text-sm font-medium text-white">Voice</p>
                    <p className="text-xs text-slate-500">{config?.voice?.name} via {config?.voice?.provider}</p>
                  </div>
                </div>
                <span className="text-xs text-emerald-400 font-medium">410ms</span>
              </div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={onStop}
              className="px-8 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-medium transition-all duration-200 purple-glow"
            >
              Back to Wizard
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-bold text-white">Voice Session</h2>
          <p className="text-slate-400 mt-1 text-sm">
            {config?.brain?.name} · {config?.voice?.name}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <StatusBadge icon={Brain} label="BRAIN" value={config?.brain?.provider || 'N/A'} color="purple" />
          <StatusBadge icon={Zap} label="LATENCY" value="1.6s" color="amber" />
          <StatusBadge icon={Globe} label="PROVIDER" value={config?.brain?.type === 'local' ? 'Local' : 'Cloud'} color="emerald" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left: VoiceWaves + Mic */}
        <div className="glass-panel p-8 flex flex-col items-center justify-center min-h-[400px]">
          <div className="w-full mb-8">
            <VoiceWaves active={micOn} />
          </div>

          <button
            onClick={toggleMic}
            className={`relative w-28 h-28 rounded-full flex items-center justify-center transition-all duration-300 ${
              micOn
                ? 'bg-emerald-600 shadow-[0_0_40px_rgba(16,185,129,0.3)]'
                : 'bg-slate-800 hover:bg-slate-700'
            }`}
          >
            {micOn && (
              <span className="absolute inset-0 rounded-full animate-pulse-ring border-2 border-emerald-500" />
            )}
            {micOn ? (
              <Mic className="w-10 h-10 text-white relative z-10" />
            ) : (
              <Mic className="w-10 h-10 text-slate-400 relative z-10" />
            )}
          </button>

          <p className="text-sm text-slate-500 mt-6">
            {micOn ? 'Listening...' : 'Click to start recording'}
          </p>
        </div>

        {/* Right: Live Logs */}
        <div className="glass-panel p-6 flex flex-col min-h-[400px]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Live Logs</h3>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${active ? 'bg-emerald-500 animate-pulse' : 'bg-slate-600'}`} />
              <span className="text-xs text-slate-500">{active ? 'Active' : 'Stopped'}</span>
            </div>
          </div>

          <div ref={logContainer} className="flex-1 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
            <AnimatePresence>
              {logs.map((log) => (
                <motion.div
                  key={log.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-start gap-3 p-2.5 rounded-lg bg-slate-800/30 animate-slide-in-right"
                >
                  <span
                    className={`text-xs font-mono font-bold px-1.5 py-0.5 rounded ${
                      log.type === 'stt'
                        ? 'text-blue-400 bg-blue-600/20'
                        : log.type === 'urp'
                        ? 'text-amber-400 bg-amber-600/20'
                        : log.type === 'brain'
                        ? 'text-purple-400 bg-purple-600/20'
                        : 'text-emerald-400 bg-emerald-600/20'
                    }`}
                  >
                    {log.label}
                  </span>
                  <p className="text-sm text-slate-300 flex-1">{log.message}</p>
                  <span className="text-xs text-slate-600 font-mono">{log.time}</span>
                </motion.div>
              ))}
            </AnimatePresence>
            {logs.length === 0 && (
              <p className="text-sm text-slate-600 text-center py-12">Waiting for input...</p>
            )}
          </div>

          <button
            onClick={handleStop}
            className="mt-4 w-full py-3 bg-red-600/80 hover:bg-red-600 text-white rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2"
          >
            <Square className="w-4 h-4" />
            Stop Session
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ icon: Icon, label, value, color }) {
  const colorMap = {
    purple: { bg: 'bg-purple-600/20', text: 'text-purple-400' },
    amber: { bg: 'bg-amber-600/20', text: 'text-amber-400' },
    emerald: { bg: 'bg-emerald-600/20', text: 'text-emerald-400' },
  };
  const c = colorMap[color] || colorMap.purple;

  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${c.bg}`}>
      <Icon className={`w-4 h-4 ${c.text}`} />
      <div>
        <p className={`text-xs font-semibold ${c.text}`}>{value}</p>
        <p className="text-[10px] text-slate-500 uppercase">{label}</p>
      </div>
    </div>
  );
}

export default VoiceSession;
